// WhatsAppInbox.jsx — CONTAINER do painel de conversas do WhatsApp (dono da lógica). Busca na API, monta
// { vm, actions } (contrato: shell/whatsappInbox.vm.js @v3) e renderiza a View do Claude Design, que chega por prop
// (`View`) — até a volta do CD ela não existe e o Layout nem monta este container (INBOX_VIEW_READY).
// Planos: docs/PLANO-EXECUCAO-CONVERSAS-WHATSAPP.md (TODO 4.5 e revisão 2) e
// docs/PLANO-EXECUCAO-RESPONDER-CLIENTE-WHATSAPP.md (TODO 4.5 — a caixa de responder).
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMediaQuery, useTheme } from '@mui/material';
import whatsappService from '../services/whatsappService';
import { useCloseOnBack } from '../hooks/useCloseOnBack';
import { buildInboxVm, mergeMessages } from './whatsappInboxVmBuilder';
import { INBOX_UNREAD_QUERY_KEY, INBOX_CONVERSATIONS_QUERY_KEY } from './whatsappInboxQueryKeys';
import { trackEvent } from '../utils/tracking';

const LIST_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const OPEN_CONVERSATION_REFRESH_MS = 20_000;   // conversa aberta: mensagem nova aparece sozinha
const AFTER_SEND_REFRESH_MS = 3_000;           // depois de enviar: "Enviando" vira "Enviada" sem esperar os 20 s
const SEND_ERROR_FALLBACK = 'Não deu para enviar. Tente de novo.';
const ENTITLEMENTS_QUERY_KEY = ['entitlements'];   // saldo do topo (useEntitlements) — mesmo padrão do PixQrCheckout

export default function WhatsAppInbox({ open, onClose, View }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Lista: busca + "carregar mais" ─────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const conversationsQuery = useInfiniteQuery({
    queryKey: [...INBOX_CONVERSATIONS_QUERY_KEY, searchQuery],
    queryFn: ({ pageParam }) => whatsappService.listConversations({ limit: LIST_PAGE_SIZE, before: pageParam, q: searchQuery }),
    initialPageParam: null,
    // Cursor = horário da última mensagem da última conversa mostrada (a API devolve as anteriores a ele).
    getNextPageParam: (last) => (last?.hasMore ? last.items?.at(-1)?.lastMessage?.createdAt ?? undefined : undefined),
    enabled: !!open,
    refetchInterval: open ? 60_000 : false,
    refetchOnWindowFocus: true,
  });
  const conversationItems = conversationsQuery.data
    ? conversationsQuery.data.pages.flatMap((p) => p?.items ?? [])
    : null;

  // ── Conversa aberta ───────────────────────────────────────────────────────
  const [selectedKey, setSelectedKey] = useState(null);
  // A conversa aberta AGORA — resposta que chega depois de o dono trocar de conversa (refresh, envio, refresh de 3 s
  // pós-envio) é descartada, senão mensagens de um cliente entrariam na conversa de outro.
  const selectedKeyRef = useRef(null);
  useEffect(() => { selectedKeyRef.current = selectedKey; }, [selectedKey]);
  const [messages, setMessages] = useState(null);        // acumuladas, mais antiga → mais nova (forma da API)
  const [hasMore, setHasMore] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [messagesError, setMessagesError] = useState(false);

  // ── Caixa de escrever (@v3) — por conversa aberta; zera ao trocar ou fechar ──
  const [composer, setComposer] = useState(null);         // `composer` da API (prazo, motivo, assinatura)
  const [composerText, setComposerText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const resetComposer = useCallback(() => {
    setComposer(null);
    setComposerText('');
    setSending(false);
    setSendError(null);
  }, []);

  const resetConversation = useCallback(() => {
    setSelectedKey(null);
    setMessages(null);
    setHasMore(false);
    setMessagesError(false);
    resetComposer();
  }, [resetComposer]);

  // Fechar o painel volta para a lista (e limpa a busca) na próxima abertura.
  useEffect(() => {
    if (!open) {
      resetConversation();
      setSearchInput('');
    }
  }, [open, resetConversation]);

  // P2: abrir a conversa (ou chegar mensagem nova com ela aberta) marca como lida. Best-effort.
  const markRead = useCallback(async (key) => {
    try {
      await whatsappService.markConversationRead(key);
      queryClient.invalidateQueries({ queryKey: INBOX_UNREAD_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: INBOX_CONVERSATIONS_QUERY_KEY });
    } catch (err) {
      console.warn('[WhatsAppInbox] não consegui marcar a conversa como lida', err);
    }
  }, [queryClient]);

  const openConversation = useCallback(async (key) => {
    setSelectedKey(key);
    setMessages(null);
    setHasMore(false);
    setMessagesError(false);
    resetComposer();
    setMessagesLoading(true);
    try {
      const page = await whatsappService.listConversationMessages(key);
      if (selectedKeyRef.current !== null && selectedKeyRef.current !== key) return;   // abriu outra no meio
      setMessages(page?.items ?? []);
      setHasMore(!!page?.hasMore);
      setComposer(page?.composer ?? null);
    } catch {
      setMessagesError(true);
      return;
    } finally {
      setMessagesLoading(false);
    }
    markRead(key);
  }, [markRead, resetComposer]);

  // Conversa aberta se atualiza sozinha: busca a página mais nova e junta com o que está na tela (mensagem nova
  // entra; tique que mudou é trocado). Se chegou recebida nova, marca como lida — o dono está olhando. O `composer`
  // também se atualiza: o prazo de 24 h fecha (ou reabre, com mensagem nova do cliente) com a conversa aberta.
  const refreshing = useRef(false);
  const refreshOpenConversation = useCallback(async (key) => {
    if (refreshing.current) return;
    refreshing.current = true;
    try {
      const page = await whatsappService.listConversationMessages(key);
      if (selectedKeyRef.current !== key) return;   // o dono trocou de conversa enquanto buscava
      const novas = page?.items ?? [];
      setMessages((prev) => mergeMessages(prev, novas));
      setComposer(page?.composer ?? null);
      if (novas.some((m) => m.direction === 'in' && m.unread)) markRead(key);
    } catch {
      // silencioso: a próxima volta tenta de novo; a conversa que já está na tela continua
    } finally {
      refreshing.current = false;
    }
  }, [markRead]);

  useEffect(() => {
    if (!open || !selectedKey || messagesLoading || messagesError) return undefined;
    const id = setInterval(() => refreshOpenConversation(selectedKey), OPEN_CONVERSATION_REFRESH_MS);
    return () => clearInterval(id);
  }, [open, selectedKey, messagesLoading, messagesError, refreshOpenConversation]);

  // Enviar: a API devolve a mensagem já "Enviando" — entra na conversa na hora, pelo mesmo merge do refresh (o id é o
  // mesmo que o Notifications grava, então o refresh TROCA a bolha em vez de duplicar). O texto só sai da caixa se deu
  // certo; com erro ele fica lá para tentar de novo.
  const sendMessage = useCallback(async () => {
    const texto = composerText.trim();
    if (!selectedKey || sending || !texto || !composer?.canSend) return;
    const key = selectedKey;
    setSending(true);
    setSendError(null);
    try {
      const item = await whatsappService.sendConversationMessage(key, texto);
      trackEvent('whatsapp_inbox_message_sent', {});
      queryClient.invalidateQueries({ queryKey: INBOX_CONVERSATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ENTITLEMENTS_QUERY_KEY });   // cada mensagem custa 1 crédito
      if (selectedKeyRef.current !== key) return;   // trocou de conversa: a mensagem saiu, só não mostramos aqui
      if (item) setMessages((prev) => mergeMessages(prev, [item]));
      setComposerText('');
      setTimeout(() => refreshOpenConversation(key), AFTER_SEND_REFRESH_MS);
    } catch (err) {
      if (selectedKeyRef.current !== key) return;
      setSendError(err?.response?.data?.error || SEND_ERROR_FALLBACK);
      // 409 = o prazo fechou ou o saldo acabou desde a última atualização: traz o motivo novo para a caixa.
      if (err?.response?.status === 409) refreshOpenConversation(key);
    } finally {
      setSending(false);
    }
  }, [composerText, selectedKey, sending, composer, queryClient, refreshOpenConversation]);

  const loadMore = useCallback(async () => {
    if (!selectedKey || !messages?.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await whatsappService.listConversationMessages(selectedKey, { before: messages[0].createdAt });
      setMessages((prev) => mergeMessages(prev, page?.items ?? []));
      setHasMore(!!page?.hasMore);
      if (page?.composer) setComposer(page.composer);
    } catch {
      setMessagesError(true);
    } finally {
      setLoadingMore(false);
    }
  }, [selectedKey, messages, loadingMore]);

  // Voltar do celular (o painel ocupa a tela toda abaixo de 600px — o mesmo corte da View): como no WhatsApp, da
  // conversa vai para a lista; da lista fecha o painel. Sem isto o voltar saía da tela (na 1ª tela, para o login).
  const theme = useTheme();
  const telaCheia = useMediaQuery(theme.breakpoints.down('sm'));
  const isOnTop = useCloseOnBack(!!open && !!View && telaCheia, () => {
    if (selectedKeyRef.current) {
      resetConversation();
      return true;   // continua aberto: o próximo voltar fecha
    }
    onClose?.();
    return false;
  });
  // Ir para outra tela de dentro do painel: com o painel na entrada atual, TROCA a entrada (replace) — voltar da
  // ficha do cliente cai na tela de antes, sem reabrir o painel nem passo morto.
  const sairPara = (href) => {
    const replace = isOnTop();
    onClose?.();
    navigate(href, { replace });
  };

  if (!View) return null;

  const vm = buildInboxVm({
    open: !!open,
    conversationItems,
    conversationsLoading: conversationsQuery.isLoading,
    conversationsError: conversationsQuery.isError,
    hasMoreConversations: !!conversationsQuery.hasNextPage,
    loadingMoreConversations: conversationsQuery.isFetchingNextPage,
    searchQuery: searchInput,
    selectedKey,
    messages,
    hasMore,
    messagesLoading,
    loadingMore,
    messagesError,
    composer,
    composerText,
    sending,
    sendError,
  });

  const actions = {
    onClose: () => onClose?.(),
    onOpenConversation: (key) => {
      trackEvent('whatsapp_inbox_conversation_open', {});
      openConversation(key);
    },
    onBackToList: resetConversation,
    onLoadMore: loadMore,
    onLoadMoreConversations: () => {
      if (conversationsQuery.hasNextPage && !conversationsQuery.isFetchingNextPage) conversationsQuery.fetchNextPage();
    },
    onSearch: (q) => setSearchInput(q ?? ''),
    onOpenPerson: (href) => sairPara(href),
    onReplyOnWhatsApp: (href) => {
      if (!href) return;
      trackEvent('whatsapp_inbox_reply_on_whatsapp', {});
      window.open(href, '_blank', 'noopener,noreferrer');
    },
    onSeeAll: () => sairPara('/whatsapp'),
    onRetry: () => (selectedKey ? openConversation(selectedKey) : conversationsQuery.refetch()),
    onComposerChange: (text) => {
      setComposerText(String(text ?? '').slice(0, composer?.maxLength ?? 1500));
      if (sendError) setSendError(null);
    },
    onSendMessage: sendMessage,
    onBuyCredits: () => {
      trackEvent('whatsapp_inbox_buy_credits', {});
      sairPara('/billing?product=whatsapp');
    },
  };

  return <View vm={vm} actions={actions} />;
}
